const OPAQUE_REF_MIN_LENGTH = 22;
const OPAQUE_REF_MAX_LENGTH = 64;
const BASE64URL_PATTERN = /^[A-Za-z0-9_-]+$/;
const CONVEX_ID_PATTERN = /^[a-z0-9]{31,32}$/;
const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

declare const opaqueRouteRefBrand: unique symbol;

/** A syntactically opaque route identifier, never display copy. */
export type OpaqueRouteRef = string & { readonly [opaqueRouteRefBrand]: true };

export type PlanSurface =
  | "today"
  /**
   * The helper's own screen, and the two deep links Epic 7 aims at it
   * (bead hf-e6-commitments-coverage-udt.9).
   *
   * `myWeekAsks` and `myWeekRechecks` are the SAME screen with a section brought to
   * the front — not separate pages. That is deliberate, and it is the whole reason
   * they are section routes rather than per-item ones: a notification about one ask
   * may be opened minutes after that ask was answered, withdrawn or lapsed, and a
   * per-item route would then land somebody on a dead end for having replied. A
   * section route lands them on the list, which correctly says there is nothing to
   * answer. A deep link that ages well is worth more than one that is precise for
   * ninety seconds.
   */
  | "myWeek"
  | "myWeekAsks"
  | "myWeekRechecks"
  | "calendar"
  | "availability"
  | "availabilityEveryone"
  | "newEvent"
  | "event"
  | "editEvent"
  | "newNeed"
  | "editNeed"
  | "need"
  | "planWeek"
  | "people"
  | "print"
  | "privacy"
  | "help"
  | "settings";

export type PlanNavigationSurface =
  "today" | "myWeek" | "calendar" | "availability" | "people" | "settings" | "help";

export type ShellRoute =
  | { readonly kind: "entry" }
  | { readonly kind: "plans" }
  | { readonly kind: "invitation" }
  | {
      readonly kind: "plan";
      readonly publicPlanRef: OpaqueRouteRef;
      readonly surface: PlanSurface;
      readonly itemRef?: OpaqueRouteRef;
    }
  | { readonly kind: "notFound" };

const NOT_FOUND_ROUTE: ShellRoute = { kind: "notFound" };

/**
 * Accept only identifiers generated by one of the application's opaque-ID sources.
 *
 * Semantic opacity is a generation contract. Convex document IDs are lowercase
 * base32-shaped values, so syntax alone cannot distinguish one from a same-length
 * human-entered string; route builders therefore only receive branded refs from
 * trusted query results. This parser remains a fail-closed gate for ordinary
 * slugs, dates, email addresses and labels.
 */
export function parseOpaqueRouteRef(value: string): OpaqueRouteRef | undefined {
  if (UUID_V4_PATTERN.test(value) || CONVEX_ID_PATTERN.test(value)) {
    return value as OpaqueRouteRef;
  }

  if (value.length < OPAQUE_REF_MIN_LENGTH || value.length > OPAQUE_REF_MAX_LENGTH) {
    return undefined;
  }

  if (!BASE64URL_PATTERN.test(value)) {
    return undefined;
  }

  if (!/[a-z]/.test(value) || !/[A-Z]/.test(value) || !/[0-9]/.test(value)) {
    return undefined;
  }

  return value as OpaqueRouteRef;
}

/** Parse only the canonical, query-free route allowlist from plan section 13.7. */
export function parseShellPath(pathname: string): ShellRoute {
  const segments = splitCanonicalPath(pathname);
  if (segments === undefined) {
    return NOT_FOUND_ROUTE;
  }

  if (segments.length === 0) {
    return { kind: "entry" };
  }

  if (segments.length === 1 && segments[0] === "plans") {
    return { kind: "plans" };
  }

  if (segments.length === 1 && segments[0] === "join") {
    return { kind: "invitation" };
  }

  if (segments[0] !== "c") {
    return NOT_FOUND_ROUTE;
  }

  const publicPlanRefValue = segments[1];
  if (publicPlanRefValue === undefined) {
    return NOT_FOUND_ROUTE;
  }

  const publicPlanRef = parseOpaqueRouteRef(publicPlanRefValue);
  if (publicPlanRef === undefined) {
    return NOT_FOUND_ROUTE;
  }

  if (segments.length === 2) {
    return { kind: "plan", publicPlanRef, surface: "today" };
  }

  if (segments.length === 3) {
    const surface = parseSimplePlanSurface(segments[2]);
    return surface === undefined ? NOT_FOUND_ROUTE : { kind: "plan", publicPlanRef, surface };
  }

  if (segments.length === 4) {
    if (segments[2] === "availability" && segments[3] === "everyone") {
      return { kind: "plan", publicPlanRef, surface: "availabilityEveryone" };
    }

    // The Epic 7 notification landing zone. An allowlist of two section names, so a
    // deep link can never carry anything but a word this file already knows.
    if (segments[2] === "my-week") {
      if (segments[3] === "asks") return { kind: "plan", publicPlanRef, surface: "myWeekAsks" };
      if (segments[3] === "rechecks") {
        return { kind: "plan", publicPlanRef, surface: "myWeekRechecks" };
      }
      return NOT_FOUND_ROUTE;
    }

    if (segments[2] === "events") {
      return parseItemRoute(publicPlanRef, segments[3], "newEvent", "event");
    }

    if (segments[2] === "needs") {
      return parseItemRoute(publicPlanRef, segments[3], "newNeed", "need");
    }
  }

  // `/events/<ref>/edit` and `/needs/<ref>/edit`. Symmetric on purpose: a need is
  // edited exactly as an event is, and a vocabulary that offered one and not the
  // other would make the asymmetry look deliberate.
  if (segments.length === 5 && segments[4] === "edit") {
    const editSurface =
      segments[2] === "events" ? "editEvent" : segments[2] === "needs" ? "editNeed" : undefined;
    if (editSurface === undefined) return NOT_FOUND_ROUTE;

    const itemRef = parseOpaqueRouteRef(segments[3] ?? "");
    return itemRef === undefined
      ? NOT_FOUND_ROUTE
      : { kind: "plan", publicPlanRef, surface: editSurface, itemRef };
  }

  return NOT_FOUND_ROUTE;
}

/** Build one of the allowlisted plan-navigation links; arbitrary segments are impossible. */
export function buildPlanNavigationPath(
  publicPlanRef: OpaqueRouteRef,
  surface: PlanNavigationSurface,
): string {
  const suffix = {
    today: "",
    myWeek: "/my-week",
    calendar: "/calendar",
    availability: "/availability",
    people: "/people",
    settings: "/settings",
    help: "/help",
  } satisfies Record<PlanNavigationSurface, string>;

  return `/c/${publicPlanRef}${suffix[surface]}`;
}

function splitCanonicalPath(pathname: string): readonly string[] | undefined {
  if (pathname === "/") {
    return [];
  }

  if (
    !pathname.startsWith("/") ||
    pathname.endsWith("/") ||
    pathname.includes("//") ||
    pathname.includes("\\") ||
    pathname.includes("%") ||
    pathname.includes("?") ||
    pathname.includes("#")
  ) {
    return undefined;
  }

  const segments = pathname.slice(1).split("/");
  return segments.every((segment) => segment.length > 0) ? segments : undefined;
}

function parseSimplePlanSurface(segment: string | undefined): PlanSurface | undefined {
  switch (segment) {
    case "calendar":
    case "availability":
    case "people":
    case "print":
    case "privacy":
    case "help":
    case "settings":
      return segment;
    case "plan-week":
      return "planWeek";
    case "my-week":
      return "myWeek";
    case undefined:
    default:
      return undefined;
  }
}

function parseItemRoute(
  publicPlanRef: OpaqueRouteRef,
  itemSegment: string | undefined,
  newSurface: "newEvent" | "newNeed",
  itemSurface: "event" | "need",
): ShellRoute {
  if (itemSegment === "new") {
    return { kind: "plan", publicPlanRef, surface: newSurface };
  }

  const itemRef = parseOpaqueRouteRef(itemSegment ?? "");
  return itemRef === undefined
    ? NOT_FOUND_ROUTE
    : { kind: "plan", publicPlanRef, surface: itemSurface, itemRef };
}
